import { useFormikWithoutDirtyForm } from "@shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { SeizableIncomeCalculationResponse, clientsApi, seizableIncomeCalculationApi } from "@/api";
import { useClientId } from "@/common/hooks/useClientId";
import { useDebouncedValue } from "@/common/hooks/useDebouncedValue";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { SeizableIncomeCalculatorFormValues } from "../SeizableIncomeCalculatorDialog.types";
import {
  EXECUTION_TYPE_MAINTENANCE,
  EXECUTION_TYPE_STANDARD,
  createSeizableIncomeValidationSchema,
  findCurrentPeriod,
  fromPeriodId,
  toPeriodId,
} from "../SeizableIncomeCalculatorDialog.utils";

const DEBOUNCE_MS = 300;

export type SeizableIncomeCalculationResult = SeizableIncomeCalculationResponse & {
  income: number;
  isMaintenanceSeizure: boolean;
  hasSpecialPayments: boolean;
  subtotalAfterStandard: number;
};

export function useSeizableIncomeCalculator() {
  const { t } = useTranslation();
  const clientId = useClientId();

  const { data: availablePeriods = [] } = useQuery({
    queryKey: [QueryKey.SeizableIncomePeriods],
    queryFn: () => seizableIncomeCalculationApi.getSeizableIncomePeriods(),
  });

  // When the calculator is opened while a client is in focus, seed its inputs from that client's
  // Einkommen tab. Same query key as the Income view, so it reuses the cached entry.
  const { data: clientIncome } = useQuery({
    queryKey: [QueryKey.AssetInventoryIncome, clientId],
    queryFn: () => clientsApi.getAssetInventoryIncome({ clientId: clientId ?? "" }),
    enabled: !!clientId,
  });
  const assessment = clientIncome?.assessment;
  const seizableIncome = assessment ? assessment.income12 + assessment.income14 : 0;

  // Periods come back newest-first and may include future ones, so the default is the one today falls in.
  const currentPeriod = findCurrentPeriod(availablePeriods) ?? availablePeriods[0];
  const defaultPeriod = currentPeriod ? toPeriodId(currentPeriod) : null;

  const validationSchema = createSeizableIncomeValidationSchema(t);

  const formik = useFormikWithoutDirtyForm<SeizableIncomeCalculatorFormValues>({
    enableReinitialize: true,
    initialValues: {
      monthlyIncome: seizableIncome > 0 ? seizableIncome : null,
      period: defaultPeriod,
      executionType: EXECUTION_TYPE_STANDARD,
      hasSpecialPayments: !assessment || assessment.income14 > 0 || assessment.income12 === 0,
      maintenanceObligationCount: assessment?.maintenanceObligationCount ?? 0,
      maintenanceSeizureCount: 0,
    },
    validationSchema,
    onSubmit: () => {
      // Pure calculator — the result is computed live, nothing is persisted.
    },
  });

  const debouncedValues = useDebouncedValue(formik.values, DEBOUNCE_MS);

  const isMaintenanceSeizure = debouncedValues.executionType === EXECUTION_TYPE_MAINTENANCE;
  const obligationCount = debouncedValues.maintenanceObligationCount ?? 0;
  const seizureCount = isMaintenanceSeizure ? (debouncedValues.maintenanceSeizureCount ?? 0) : 0;

  // Validate the exact snapshot that will be sent (not the live form) against the same schema, so the
  // gate stays in sync with the field rules without duplicating them.
  const canCalculate = validationSchema.isValidSync(debouncedValues);

  const { data: result } = useQuery({
    queryKey: [QueryKey.SeizableIncomeCalculation, debouncedValues],
    queryFn: async () => {
      const response = await seizableIncomeCalculationApi.calculateSeizableIncome({
        monthlyIncome: debouncedValues.monthlyIncome ?? 0,
        asOf: debouncedValues.period != null ? fromPeriodId(debouncedValues.period) : undefined,
        maintenanceObligationCount: obligationCount,
        maintenanceSeizureCount: seizureCount,
        hasSpecialPayments: debouncedValues.hasSpecialPayments,
        isMaintenanceSeizure,
      });

      // Bundle the inputs the result panel renders together with the response, so the whole package
      // updates atomically: when an input changes, the panel only switches once the new result is
      // ready — never a fresh income/label next to stale § 291a/§ 291b amounts.
      return {
        ...response,
        income: debouncedValues.monthlyIncome ?? 0,
        isMaintenanceSeizure,
        hasSpecialPayments: debouncedValues.hasSpecialPayments,
        // For an Unterhaltsexekution the seizable amount splits into the § 291a portion and the
        // additional § 291b portion; the subtotal is what remains after § 291a (= verbleiben + § 291b).
        subtotalAfterStandard: response.retainedAmount + response.maintenanceAdditionalAmount,
      };
    },
    enabled: canCalculate,
    // Keep the previous result on screen while recalculating so the panel doesn't blank out on each keystroke.
    placeholderData: keepPreviousData,
  });

  return { formik, result, availablePeriods };
}
